'use strict';

import { listenDOMEvent } from '@jspartials/dom-api-utils';
import {
  classes,
  addClassToElement,
  removeClassToElement,
  toggleClassToElement,
  hasClassElement
} from '@jspartials/class-list-utils';
import {
  setHtmlScrollable,
  setHtmlUnscrollable,
  customEvent
} from '@jspartials/general-utils';

const selector = {
  classes: {
    activeLink: 'active-link',
    isVisibleMenuMobile: 'is-visible-menu-mobile',
    justifyCenter: 'tw-justify-center',
    menuLink: 'js-menu__link',
    firstLevel: 'first-level'
  },
  customerArea: '.js-customer-area',
  header: {
    headerContent: '.js-header__content',
    accountButton: '.js-header__account-button'
  },
  carousel: {
    imagesCarouselNav: '.js-images-carousel__nav',
    containerListSubmenuCarousel: '.js-container-list-submenu-images',
    submenuCarouselListItem: '.js-submenu-carousel__list-item',
    sliderSubmenuButtonRight: '.js-slide-right',
    sliderSubmenuButtonLeft: '.js-slide-left'
  },
  menu: {
    menuMobile: '.js-menu-mobile',
    menuListItemMobile: '.js-menu__list-item-mobile',
    menuListItemSticky: '.js-menu__list-item--sticky',
    menuDesktop: '.js-menu--desktop',
    menuListItem: '.js-menu__list-item',
    menuLink: '.js-menu__link',
    menuInputCheckbox: '.js-menu-checkbox',
    buttonOpenMenuMobile: '.js-button-open-menu-mobile'
  },
  submenu: {
    headerButtonBack: '.js-submenu__header-button-back',
    headerButtonNext: '.js-submenu__header-button-next',
    mobile: '.js_submenu__mobile',
    desktop: '.js_submenu__desktop',
    headerName: '.js-submenu__header-name',
    expander: '.js-submenu__expander'
  },
  listSubmenuImages: '.js-list-submenu-images',
  visibleDesktop: '.js-visible-desktop',
  submenuShow: '.js-show__submenu',
  columnsSubmenu: '.js-columns-submenu',
  secondLevel: '.second-level'
};

// Elements
const headerContent =
  document.querySelector(selector.header.headerContent) || null;
const menuMobile = document.querySelector(selector.menu.menuMobile) || null;
const buttonOpenMenuMobile =
  document.querySelector(selector.menu.buttonOpenMenuMobile) || null;
const menuInputCheckbox =
  document.querySelectorAll(selector.menu.menuInputCheckbox) || [];
const menuListItem =
  document.querySelectorAll(selector.menu.menuListItemMobile) || [];
const menuListItemSticky =
  document.querySelector(selector.menu.menuListItemSticky) || null;
const imagesCarouselNav =
  document.querySelectorAll(selector.carousel.imagesCarouselNav) || [];
const containerListSubmenuCarousel =
  document.querySelectorAll(selector.carousel.containerListSubmenuCarousel) ||
  [];
const submenusShow = document.querySelectorAll(selector.submenuShow) || [];
const closeSearcherFromMenu = new CustomEvent(
  customEvent.closeSearcherFromMenu
);
const configStickyMenu = {
  root: menuMobile,
  threshold: [0.0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]
};
const submenusHeaderButtonBack =
  document.querySelectorAll(selector.submenu.headerButtonBack) || [];
const submenusHeaderButtonNext =
  document.querySelectorAll(selector.submenu.headerButtonNext) || [];
const IMAGE_CAROUSEL_PIXELS = 310;
let observer = null;
const headerAccountButton =
  document.querySelector(selector.header.accountButton) || null;

/**
 * Manage when the submenu is open on desktop to prevent weird behaviour
 */
const loadMenuListItemOpenEvent = () => {
  const menuListItemsLinks = document.querySelectorAll(
    `${selector.menu.menuDesktop} ${selector.menu.menuLink}`
  );
  const menuListItems = document.querySelectorAll(
    `${selector.menu.menuDesktop} ${selector.menu.menuListItem}`
  );

  menuListItemsLinks.forEach((menuListItemsLink) => {
    listenDOMEvent(menuListItemsLink, 'mouseenter', function () {
      const menuActiveSection = menuListItemsLink.closest(
        selector.menu.menuListItem
      );
      initMenuListContentAnimationDelay(menuActiveSection);
      addClassToElement(menuListItemsLink, selector.classes.activeLink);
    });
  });

  menuListItems.forEach((menuListItem) => {
    listenDOMEvent(menuListItem, 'mouseleave', function () {
      removeClassToElement(
        menuListItem.querySelector(selector.menu.menuLink),
        selector.classes.activeLink
      );
      removeMenuListContentAnimationDelay(menuListItems);
    });
  });
};

const initMenuListContentAnimationDelay = (menuActiveSection) => {
  if (!menuActiveSection) return;

  const menuListItemsContent = menuActiveSection.querySelectorAll(
    selector.columnsSubmenu
  );

  const productTileKeyframe = [
    { transform: 'translateY(20px)', opacity: '0' },
    { transform: 'translateY(0px)', opacity: '1' }
  ];
  menuListItemsContent.forEach((menuListItemContent, i) => {
    menuListItemContent.animate(productTileKeyframe, {
      duration: 250,
      delay: i * 60,
      easing: 'cubic-bezier(0.64, 0, 0.34, 1)',
      fill: 'both'
    });
  });
};

const removeMenuListContentAnimationDelay = (menuListItemsDesktop = []) => {
  menuListItemsDesktop.forEach((menuListItem) => {
    const menuListItemsContent = menuListItem.querySelectorAll(
      selector.columnsSubmenu
    );

    menuListItemsContent.forEach((menuListItemContent, i) => {
      menuListItemContent.style.transitionDelay = 0 + 'ms';
    });
  });
};

/**
 * Show menu mobile
 */
const openMenuMobile = () => {
  if (!hasClassElement(headerContent, selector.classes.isVisibleMenuMobile)) {
    document.dispatchEvent(closeSearcherFromMenu); // Close Searcher if Menu Open
  }

  toggleClassToElement(buttonOpenMenuMobile, classes.is.visible);
  toggleClassToElement(menuMobile, classes.is.visible);
  toggleClassToElement(headerContent, selector.classes.isVisibleMenuMobile);

  if (!hasClassElement(menuMobile, classes.is.visible)) {
    setHtmlScrollable();
    const allActiveElements =
      menuMobile.querySelectorAll(`.${classes.is.active}`) || [];
    const allActiveElementsReverse = Array.from(allActiveElements).reverse();
    allActiveElementsReverse.forEach((element) => {
      removeClassToElement(element, classes.is.active);
    });
  } else {
    setHtmlUnscrollable();
  }

  menuMobile.focus();
};

/**
 * Mark current menu list item according with current page
 */
const markCurrentListItem = () => {
  const idCurrentPage = window.location.href;
  let currentMenuLink = headerContent
    ? headerContent.querySelector('a[href="' + idCurrentPage + '"]')
    : null;
  let parentCurrentMenuLink = null;
  let currentMenuLinkClosestParent = null;

  if (currentMenuLink) {
    addClassToElement(currentMenuLink, classes.is.current);
    currentMenuLinkClosestParent = currentMenuLink.closest(
      selector.menu.menuListItem
    );
    if (currentMenuLinkClosestParent) {
      parentCurrentMenuLink = currentMenuLinkClosestParent.querySelector(
        selector.menu.menuLink
      );
    }
    if (parentCurrentMenuLink) {
      addClassToElement(parentCurrentMenuLink, classes.is.current);
    }
  }
};

const moveCarouselImagesSubmenu = (list, distance) => {
  const listParent = list.parentElement;
  const navRight = listParent.querySelector(
    selector.carousel.sliderSubmenuButtonRight
  );
  const navLeft = listParent.querySelector(
    selector.carousel.sliderSubmenuButtonLeft
  );
  list.scrollBy({
    top: 0,
    left: -distance,
    behavior: 'smooth'
  });

  setTimeout(() => {
    if (list.scrollLeft > 0) {
      addClassToElement(navLeft, classes.is.visible);
    }

    // Scroll left ends
    if (list.scrollLeft === 0) {
      removeClassToElement(navLeft, classes.is.visible);
      addClassToElement(navRight, classes.is.visible);
    }

    // Scroll right ends
    if (list.scrollLeft >= list.scrollWidth - list.clientWidth) {
      removeClassToElement(navRight, classes.is.visible);
    } else {
      addClassToElement(navRight, classes.is.visible);
    }
  }, 500);
};

const resizeImagesCarousel = (item) => {
  const itemCarousel = item.querySelector(selector.listSubmenuImages);
  const itemsCarousel = itemCarousel
    ? itemCarousel.querySelectorAll(selector.visibleDesktop)
    : false;
  const itemsCount = itemsCarousel ? itemsCarousel.length : 0;
  const navRight = item.querySelector(
    selector.carousel.sliderSubmenuButtonRight
  );
  if (itemsCount > 0) {
    if (itemsCount * IMAGE_CAROUSEL_PIXELS < window.innerWidth) {
      addClassToElement(itemCarousel, selector.classes.justifyCenter);
      removeClassToElement(navRight, classes.is.visible);
    } else {
      removeClassToElement(itemCarousel, selector.classes.justifyCenter);
      addClassToElement(navRight, classes.is.visible);
    }
  }
};

const selectStep = (dataId, option) => {
  if (option === 'next') {
    addClassToElement(
      menuMobile.querySelector(`[data-menu-screen="${dataId}"]`),
      classes.is.active
    );
  } else if (option === 'prev') {
    removeClassToElement(
      menuMobile.querySelector(`[data-menu-screen="${dataId}"]`),
      classes.is.active
    );
  }

  if (!dataId.includes('menu-item')) {
    const category =
      menuMobile.querySelectorAll(`[data-menu-screen="${dataId}"]`) || [];
    if (category.length > 1) {
      category.forEach((element) => {
        const categoryParent = element.closest(selector.submenu.expander);
        if (hasClassElement(categoryParent, classes.is.active)) {
          if (option === 'next') {
            addClassToElement(
              categoryParent.querySelector(`[data-menu-screen="${dataId}"]`),
              classes.is.active
            );
          } else if (option === 'prev') {
            removeClassToElement(
              categoryParent.querySelector(`[data-menu-screen="${dataId}"]`),
              classes.is.active
            );
          }
        }
      });
    }
  }
};

/**
 * Listen header account button click event
 * @returns {void}
 */
const listenHeaderAccountButton = () => {
  if (headerAccountButton) {
    listenDOMEvent(headerAccountButton, 'click', handleClickAccountButton);
  }
};

/**
 * Handle click on account button in header
 * @param {Event} event - Event object from the click
 * @returns {void}
 */
const handleClickAccountButton = (event) => {
  const accountButton = event.target.closest(selector.header.accountButton);
  const sidebarDrawer = accountButton?.dataset?.sidebarDrawer;
  const redirectUrl = accountButton?.dataset?.redirect;
  const isLoginAndSignupPage = accountButton?.dataset?.isLoginAndSignupPage;
  const isLoginAndSignupPageValue = isLoginAndSignupPage
    ? JSON.parse(isLoginAndSignupPage)
    : false;
  const isCustomerAreaPage =
    document.querySelector(selector.customerArea) || null;

  if (redirectUrl && (isLoginAndSignupPageValue || isCustomerAreaPage)) {
    window.location.href = redirectUrl;
  } else {
    document.dispatchEvent(
      new CustomEvent(customEvent.sidebar.open, {
        detail: {
          drawer: sidebarDrawer,
          trigger: accountButton
        }
      })
    );
  }
};

document.addEventListener('DOMContentLoaded', function () {
  document.addEventListener('closeMenuFromSearcher', () => {
    if (hasClassElement(headerContent, selector.classes.isVisibleMenuMobile)) {
      openMenuMobile();
    }
  });

  // Load menu list item open event
  loadMenuListItemOpenEvent();

  // Listen header account button click event
  listenHeaderAccountButton();

  if (imagesCarouselNav !== undefined) {
    imagesCarouselNav.forEach((button) => {
      listenDOMEvent(button, 'click', function () {
        const dir = this.dataset.direction;
        const list = this.parentElement.querySelector(
          selector.listSubmenuImages
        );
        let distance = list.querySelector(
          selector.carousel.submenuCarouselListItem
        ).clientWidth;
        if (dir === 'right') {
          distance = -distance;
        }
        moveCarouselImagesSubmenu(list, distance);
      });
    });
  }

  // Mark current menu list item
  markCurrentListItem();

  if (containerListSubmenuCarousel) {
    containerListSubmenuCarousel.forEach((carousel) => {
      resizeImagesCarousel(carousel);
    });
  }

  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      let visibleMenu = Math.floor(entry.intersectionRatio * 100);
      if (visibleMenu == 0) {
        if (!hasClassElement(menuListItemSticky, classes.is.sticky)) {
          addClassToElement(menuListItemSticky, classes.is.sticky);
        }
      } else {
        if (hasClassElement(menuListItemSticky, classes.is.sticky)) {
          removeClassToElement(menuListItemSticky, classes.is.sticky);
        }
      }
    });
  }, configStickyMenu);

  if (menuListItemSticky) {
    observer.observe(menuListItem[1]);
  }

  if (menuInputCheckbox.length > 0) {
    menuInputCheckbox.forEach((menuInputCheckbox) => {
      listenDOMEvent(menuInputCheckbox, 'change', function () {
        let inputLink = menuInputCheckbox
          .closest(selector.menu.menuListItem)
          .getElementsByClassName(selector.classes.menuLink)[0];
        if (menuInputCheckbox.checked) {
          addClassToElement(inputLink, classes.is.active);
        } else {
          removeClassToElement(inputLink, classes.is.active);
        }
      });
    });
  }

  if (submenusShow.length > 0) {
    submenusShow.forEach((submenuSelected) => {
      listenDOMEvent(submenuSelected, 'click', function () {
        const submenuParent =
          submenuSelected.closest(selector.menu.menuListItem) || null;
        let submenuShowedName = submenuParent?.dataset?.name || '';
        let submenuShowedScreen = submenuParent?.dataset?.screen || '';

        const submenuShowed =
          submenuParent.querySelector(selector.submenu.mobile) || null;

        const expander =
          submenuShowed.querySelector(selector.submenu.expander) || null;
        expander.dataset.menuScreen = submenuShowedScreen;
        const headerButtonBack =
          submenuShowed.querySelector(selector.submenu.headerButtonBack) ||
          null;
        headerButtonBack.dataset.screen = submenuShowedScreen;

        const submenuHeaderButtonName =
          submenuShowed.querySelector(selector.submenu.headerName) || null;
        submenuHeaderButtonName.innerText = submenuShowedName;
      });
    });

    submenusHeaderButtonBack.forEach((button) => {
      listenDOMEvent(button, 'click', function () {
        selectStep(button.dataset.screen, 'prev');
      });
    });

    submenusHeaderButtonNext.forEach((button) => {
      listenDOMEvent(button, 'click', function () {
        selectStep(button.dataset.screen, 'next');
      });
    });
  }
});
